import React, { useEffect, useRef, useState } from "react";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import { createPortal } from "react-dom";

const NmProgressHelpDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen((s) => !s);
  const drawerContentRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (drawerContentRef.current && !drawerContentRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const drawer = (
    <div className="drawer-portal">
      <Drawer
        open={isOpen}
        onClose={toggle}
        direction="right"
        size={500}
        duration={400}
        overlayOpacity={0.5}
        overlayColor="#000"
        customIdSuffix="help-guide-nm-progress"
        className="help-guide-drawer"
        style={{ top: "73px", height: "calc(100vh - 73px)", transition: "all 0.4s ease-in-out" }}
      >
        <div className="drawer-content" ref={drawerContentRef}>
          <div className="drawer-header">
            <div className="header-title">
              <span className="emoji">🟠</span>
              <span className="title">Near Miss – In Progress Tab Help</span>
            </div>
            <button className="close-button" onClick={toggle} aria-label="Close help panel">
              &times;
            </button>
          </div>

          <div className="drawer-body">
  <p>
    The Pending Near Miss (NM) Detail Page allows you to view and manage Near Miss records where the
    investigation is yet to start. This page provides complete visibility of reported details, activity
    information, incident specifics, and team collaboration features to initiate and track the
    investigation.
  </p>

  <h5 className="section-title">Record Overview</h5>
  <p>At the top of the page, you will see:</p>
  <ul className="steps-list">
    <li>• Record Number – e.g., 940</li>
    <li>• NM Type – UA / UC (Unsafe Act / Unsafe Condition)</li>
    <li>• Activity Name – e.g., Bar Bending</li>
    <li>• NM Reported Month – e.g., July</li>
  </ul>

  <p>A timeline is displayed below, showing:</p>
  <ul className="steps-list">
    <li>• Reported details</li>
    <li>• Incident date</li>
    <li>• Involved person name(s)</li>
    <li>• Discussion comments (if any)</li>
  </ul>

  <h5 className="section-title">Reported Details Section</h5>
  <ul className="steps-list">
    <li>• Evidence – uploaded NM images.</li>
    <li>• Incident Description – narrative of the NM.</li>
    <li>• Hazard Type – classification of the hazard involved.</li>
    <li>• Risk Level – risk severity associated with the NM.</li>
  </ul>

  <h5 className="section-title">Activity Information Section</h5>
  <ul className="steps-list">
    <li>• Service and its respective Plant/Project/Client for which the NM is reported.</li>
    <li>• Department/Contractor/Location linked with the NM.</li>
  </ul>

  <h5 className="section-title">Incident Details Section</h5>
  <ul className="steps-list">
    <li>• Incident Date – when the NM occurred.</li>
    <li>• Incident Time – time of occurrence.</li>
    <li>• Involved Person Details – individuals present during the NM.</li>
  </ul>

  <h5 className="section-title">Discussion Section</h5>
  <p>The discussion section acts as a team chat board where investigation team members can:</p>
  <ul className="steps-list">
    <li>• Share insights, comments, and observations.</li>
    <li>• Collaborate to decide how to investigate the NM.</li>
    <li>• Work towards preventing recurrence and reducing future injuries or incidents.</li>
  </ul>
  <p>This team collaboration approach keeps the investigation focused and well-documented.</p>

  <h5 className="section-title">UAUC Detail Analysis (Right-hand Panel)</h5>
  <p>On the right-hand side of the page, you will see data-driven insights to support the investigation, including:</p>
  <ul className="steps-list">
    <li>• IRA Analysis – accident statistics.</li>
    <li>• Near Miss Analysis – trends in NM reporting.</li>
    <li>• UA vs UC Analysis – unsafe act vs unsafe condition data.</li>
    <li>• Risk-Based Analysis – high/medium/low risk patterns.</li>
    <li>• Good Act & TBT Count – safety-positive actions and toolbox talks.</li>
    <li>• Internal & External Training Count – training conducted.</li>
    <li>• Employee & Contractor Training Count – training coverage for both groups.</li>
  </ul>
  <p>
    This analytical data enables the team to develop strong, evidence-based strategies to reduce UAUC
    incidents.
  </p>

  <h5 className="section-title">Steps to Start NM Investigation</h5>

  <b>Step 1 – Assign Investigation Team</b>
  <ul className="steps-list">
    <li>• Go to the top right corner of the page.</li>
    <li>• Click on the Add Team icon.</li>
    <li>• Add investigation team members.</li>
    <li>
      • Once added, all team members will appear in the Investigation Team section above the Analysis
      tab (right side).
    </li>
  </ul>

  <b>Step 2 – Assign Team Lead</b>
  <ul className="steps-list">
    <li>• In the Investigation Team list, each member has a crown icon next to their name.</li>
    <li>• To assign a Team Lead, click on the crown icon.</li>
    <li>
      • The selected member will be marked as the Team Lead, responsible for guiding the investigation.
    </li>
  </ul>

  <b>Step 3 – Set Target Date</b>
  <ul className="steps-list">
    <li>• Once a Team Lead is assigned, a pop-up will appear.</li>
    <li>
      • Enter the Target Date – the deadline for completing the investigation up to CAPA (Corrective
      and Preventive Action) recommendation.
    </li>
    <li>• This ensures tracking from Reported Date → Investigation → CAPA Implementation.</li>
  </ul>

  <div className="alert alert-info">
    <b>✅ Note:</b> Assigning a Team Lead and setting a target date are mandatory to start the NM
    investigation.
  </div>
</div>

        </div>
      </Drawer>
    </div>
  );

  return (
    <>
      <div
        className="toggle-help-btn"
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === "Enter" || e.key === " ") toggle();
        }}
        style={{ fontSize: "16px", cursor: "pointer" }}
      >
        Page Help
        <img src="/images/formkit_help.svg" alt="" />
      </div>
      {createPortal(drawer, document.body)}
    </>
  );
};

export default NmProgressHelpDrawer;
